import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { AuthProvider } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard.jsx';
import Customers from './pages/Customers.jsx';
import Jobs from './pages/Jobs.jsx';
import CreateJobs from './pages/create-jobs.jsx';
import EditJobs from './pages/edit-jobs.jsx';
import EditCustomers from './pages/edit-customers.jsx';
import CreateCustomers from './pages/create-customers.jsx';
import Planning from './pages/Planning.jsx';
import Profile from './pages/Profile.jsx';
import Users from './pages/Users.jsx';
import EditUsers from './pages/edit-users.jsx';
import CreateUsers from './pages/create-users.jsx';
import Settings from './pages/settings.jsx';
import Info from './pages/Info.jsx';
import Login from './pages/LoginPage.jsx';
import DrawerRouterContainer from './components/DrawerRouterContainer.jsx';
import { AppContext } from './AppContext';
import { IntlProvider, load, LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import weekData from 'cldr-core/supplemental/weekData.json';
import numbers from 'cldr-numbers-full/main/de/numbers.json';
import currencies from 'cldr-numbers-full/main/de/currencies.json';
import caGregorian from 'cldr-dates-full/main/de/ca-gregorian.json';
import dateFields from 'cldr-dates-full/main/de/dateFields.json';
import timeZoneNames from 'cldr-dates-full/main/de/timeZoneNames.json';
import deMessages from './messages/de-DE.js';
import 'hammerjs';
import './App.scss';

// Lade deutsche CLDR-Daten und Nachrichten
load(likelySubtags, currencyData, weekData, numbers, currencies, caGregorian, dateFields, timeZoneNames);
loadMessages(deMessages, 'de-DE');

// Definiere die benötigten Google Maps Bibliotheken
const libraries = ['places', 'geometry', 'drawing', 'marker']; // 'marker' hinzugefügt

const PrivateLayout = () => (
  <DrawerRouterContainer>
    <Outlet />
  </DrawerRouterContainer>
);

const App = () => {
  const [contextState, setContextState] = React.useState({
    firstName: 'Phillip',
    lastName: 'Schmitt',
    middleName: '',
    email: 'ps@exploserv.com',
    phoneNumber: '(+1) 8373-837-93-02',
    avatar: null,
    country: 'Deutschland',
    isInPublicDirectory: true,
    biography: '',
    teamId: 1,
  });

  const [notifications, setNotifications] = React.useState([]);

  const showNotification = (type, message) => {
    setNotifications((prev) => [
      ...prev,
      {
        id: Date.now(),
        type,
        message,
      },
    ]);
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const onProfileChange = React.useCallback(
    (event) => {
      setContextState({ ...contextState, ...event.dataItem });
    },
    [contextState, setContextState]
  );

  // Initialisiere den Google Maps API Loader
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyCrN9qaWxzLiwIpYx9LqKbHr9trrwvlvT8', // Dein API-Schlüssel
    libraries, // ['places', 'geometry', 'drawing', 'marker']
  });

  // Wenn die API noch nicht geladen ist, zeige einen Ladebildschirm
  if (!isLoaded) {
    return <div>Loading Google Maps API...</div>;
  }

  return (
    <div className="App">
      <LocalizationProvider language="de-DE">
        <IntlProvider locale="de">
          <AppContext.Provider
            value={{
              ...contextState,
              onProfileChange,
              notifications,
              setNotifications,
              showNotification,
              removeNotification,
              isGoogleMapsLoaded: isLoaded,
            }}
          >
            <BrowserRouter basename="/exploserv-btb">
              <AuthProvider>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route element={<PrivateLayout />}>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/customers" element={<Customers />} />
                    <Route path="/create-customers" element={<CreateCustomers />} />
                    <Route path="/edit-customers/:customerID" element={<EditCustomers />} />
                    <Route path="/jobs" element={<Jobs />} />
                    <Route path="/create-jobs" element={<CreateJobs />} />
                    <Route path="/edit-jobs/:jobID" element={<EditJobs />} />
                    <Route path="/planning" element={<Planning />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/edit-users/:BenutzerID" element={<EditUsers />} />
                    <Route path="/create-users" element={<CreateUsers />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/info" element={<Info />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
              </AuthProvider>
            </BrowserRouter>
          </AppContext.Provider>
        </IntlProvider>
      </LocalizationProvider>
    </div>
  );
};

export default App;